import { assert, fail } from '#app/assert'

import type {
  ColspecComponent,
  ComplexParagraphComponent,
  CosimaRoot,
  EditionDetails,
  EntryComponent,
  FigureComponent,
  HighlightComponent,
  IdNode,
  ImageComponent,
  LegendComponent,
  LegendEntryComponent,
  LegendEntryDescriptionComponent,
  LegendEntrySyComponent,
  ListItemComponent,
  MetaFigureComponent,
  NoTranslateComponent,
  ParagraphComponent,
  PkBase,
  PkBenefits,
  PkBlock,
  PkDescription,
  PkMeta,
  PkShortDescription,
  PkTechData,
  RowComponent,
  SpecialCharacterComponent,
  SubComponent,
  SubtitleComponent,
  SupComponent,
  TableBodyComponent,
  TableComponent,
  TableGroupComponent,
  TableHeadComponent,
  TextVariableDefinition,
  TopLevelComponent,
  UnknownComponent,
  UnorderedListComponent,
  ValidityEntry,
  VCodeLinkComponent,
} from './document'

const assertChildrenExist = (element: Element, childnames: string[]) => {
  let filterTargets = [...childnames]
  for (const child of element.childNodes.values()) {
    filterTargets = filterTargets.filter((x) => x !== child.nodeName)
  }
  assert(filterTargets.length <= 0, `Expected [${childnames.join(',')}] in node '${element.nodeName}' but did not find [${filterTargets.join(',')}]`)
}

/** exact one of matching - of all children, exactly one has match _one of the names_ passed, but not more */
const assertChildrenContainsExactlyOne = (element: Element, oneOfNames: string[]) => {
  const names: string[] = []
  for (const child of element.children) {
    names.push(child.nodeName)
  }
  const existingNames = oneOfNames.filter((x) => names.includes(x))
  assert(
    existingNames.length === 1,
    `Expected node '${element.nodeName}' to contain exactly one of either [${oneOfNames.join(',')}], but found ${existingNames.length}`,
  )
  const existingName = existingNames[0]
  const nameRepitions = names.filter((x) => x === existingName)
  assert(
    nameRepitions.length === 1,
    `Expected node '${element.nodeName} to contain exactly one of '${existingName}', but found ${nameRepitions.length}`,
  )
}

const assertAttributesExist = (element: Element, attributeNames: string[]) => {
  const attributes = element.getAttributeNames()
  assert(attributeNames.every((x) => attributes.includes(x)))
}

const assertNodeProperties = (
  element: Element | undefined,
  {
    attributeNames,
    childNames,
  }: {
    attributeNames?: string[]
    childNames?: string[]
  },
) => {
  if (!element) {
    fail('Element was not defined')
    return
  }
  if (childNames) {
    assertChildrenExist(element, childNames)
  }
  if (attributeNames) {
    assertAttributesExist(element, attributeNames)
  }
}

const attributeStringOptional = (element: Element, attributeName: string) => {
  return element.attributes.getNamedItem(attributeName)?.value
}

const attributeString = (element: Element, attributeName: string) => {
  const item = attributeStringOptional(element, attributeName)
  assert(item !== undefined, `Expected attribute '${attributeName}' in node '${element.nodeName}'`)
  return item ?? 'NOT FOUND'
}

const attributeNumberOptional = (element: Element, attributeName: string) => {
  const text = attributeStringOptional(element, attributeName)
  return text ? Number.parseFloat(text) : undefined
}

const attributeNumber = (element: Element, attributeName: string) => {
  const text = attributeString(element, attributeName)
  return Number.parseFloat(text)
}

const attributeBoolean = (element: Element, attributeName: string) => {
  const text = attributeString(element, attributeName)
  if (text === 'true') {
    return true
  }
  if (text === 'false') {
    return false
  }
  return undefined
}

const getElementChildOptional = (element: Element, nodeName: string): Element | undefined => {
  for (const child of element.children) {
    if (child.nodeName === nodeName) {
      return child
    }
  }

  return undefined
}

const getElementChild = (element: Element, nodeName: string): Element => {
  const child = getElementChildOptional(element, nodeName)
  if (!child) {
    throw new Error(`There should have been a child of '${nodeName}' in '${element.nodeName}', but there was none found.`)
  }
  return child
}

const getElementChildren = (element: Element, nodeName: string) => {
  const found: Element[] = []
  for (const child of element.children) {
    if (child.nodeName === nodeName) {
      found.push(child)
    }
  }
  return found
}

export const cosimaLexing = (document: Document): CosimaRoot | undefined => {
  const root = document.children[0]
  return processRoot(root)
}

const expectedRootAttributes = ['y.io.id', 'y.io.version', 'y.io.language', 'y.io.variant', 'y.io.format']
const expectedRootNodes = ['edition-details']
const processRoot = (rootElement?: Element) => {
  assertNodeProperties(rootElement, { attributeNames: expectedRootAttributes, childNames: expectedRootNodes })
  if (!rootElement) {
    return undefined
  }
  const editionDetails = getElementChild(rootElement, 'edition-details')
  const nodes = getElementChildren(rootElement, 'node')
  return {
    id: attributeString(rootElement, 'y.io.id'),
    version: attributeNumber(rootElement, 'y.io.version'),
    language: attributeString(rootElement, 'y.io.language'),
    variant: attributeString(rootElement, 'y.io.variant'),
    format: attributeString(rootElement, 'y.io.format'),
    editionDetails: processEditionDetails(editionDetails),
    nodes: processNodes(nodes),
  } satisfies CosimaRoot
}

const processEditionDetails = (element: Element) => {
  const pkCode = getElementChildOptional(element, 'PK-code')
  const validityEntries = getElementChildren(element, 'iovalidityentry').map(
    (x) =>
      ({
        id: attributeString(x, 'id'),
        validity: attributeString(x, 'validity'),
      }) satisfies ValidityEntry,
  )
  const dateValidFrom = getElementChildOptional(element, 'pk_date_valid_from')
  return {
    code: pkCode?.textContent ?? undefined,
    validity: validityEntries,
    validFrom: dateValidFrom?.getAttribute('value') ?? undefined,
  } satisfies EditionDetails
}

const processNodes = (elements: Element[]) => {
  const nodes: Partial<Record<string, PkBase>> = {}
  for (const element of elements) {
    const node = processNode(element)
    if (!node) {
      fail('Processing of a node should have yielded a result')
      continue
    }
    nodes[node.type] = node
  }

  return nodes
}

const processNode = (element: Element) => {
  const child = element.children[0]
  if (child.nodeName === 'PK-title') {
    return processComplexNode(element)
  }
  return processSimpleNode(element)
}

const processSimpleNode = (element: Element) => {
  assertChildrenContainsExactlyOne(element, [
    'PK-meta',
    'PK-short-description',
    'PK-benefits',
    'PK-description',
    'PK-techdata',
    'PK-functions',
    'PK-notes',
  ])
  const child = element.children[0]
  return processPkNode(child)
}

const processComplexNode = (element: Element): PkBase | undefined => {
  assertNodeProperties(element, { childNames: ['node'] })
  const childNode = getElementChild(element, 'node')
  return processNode(childNode)
}

const requiredPkBaseAttributes = [
  'y.id',
  'y.io.id',
  'y.io.version',
  'y.io.language',
  'y.io.variant',
  'y.io.format',
  'y.io.type',
  'y.commentable',
  'y.io.validity.mode',
  'y.validity.mode',
  'y.io.validity.ids',
  'y.io.validity.text',
]
const processPkNode = (element: Element) => {
  switch (element.nodeName) {
    case 'PK-meta':
      return processPkMeta(element)
    case 'PK-short-description':
      return processPkShortDescription(element)
    case 'PK-benefits':
      return processPkBenefits(element)
    case 'PK-description':
      return processPkDescription(element)
    case 'PK-techdata':
      return processPkTechdata(element)
    case 'PK-notes':
      return processPkNotes(element)
    case 'PK-functions':
      return processPkFunctions(element)
    default:
      fail(`Passed a note of type '${element.nodeName}' that has no processor.`)
  }
  return undefined
}

const processPkBase = <T extends string>(element: Element) => {
  assertNodeProperties(element, { attributeNames: requiredPkBaseAttributes })
  return {
    baseId: attributeString(element, 'y.id'),
    id: attributeString(element, 'y.io.id'),
    version: attributeNumber(element, 'y.io.version'),
    language: attributeString(element, 'y.io.language'),
    variant: attributeString(element, 'y.io.variant'),
    format: attributeString(element, 'y.io.format'),
    type: attributeString(element, 'y.io.type') as T,
    commentable: attributeBoolean(element, 'y.commentable') ?? false,
    validityMode: attributeString(element, 'y.io.validity.mode'),
    validityIds: attributeString(element, 'y.io.validity.ids'),
    validityText: attributeString(element, 'y.io.validity.text'),
  } satisfies PkBase
}

const processPkMeta = (element: Element): PkMeta => {
  assertNodeProperties(element, { childNames: ['PK-code', 'PK-AVIS-code-name', 'PK-COSIMA-code-name', 'meta-figure'] })

  const pkCode = getElementChildOptional(element, 'PK-code')
  const pkAvisCodeName = getElementChildOptional(element, 'PK-AVIS-code-name')
  const pkCosimaCodeName = getElementChildOptional(element, 'PK-COSIMA-code-name')
  const metaFigure = getElementChildOptional(element, 'meta-figure')
  const textVariableDefinition = getElementChildOptional(element, 'y.textvar-defs')

  return {
    ...processPkBase(element),
    pkCode: processPkCode(pkCode),
    pkAvisCodeName: processAvisCodeName(pkAvisCodeName),
    pkCosimaCodeName: processCosimaCodeName(pkCosimaCodeName),
    textVariableDefinition: processTextVariableDefinition(textVariableDefinition),
    figure: processMetaFigure(metaFigure),
  }
}

const processIdNode = (element?: Element): IdNode => {
  if (!element) {
    return {
      id: 'NOT FOUND',
      content: 'NOT FOUND',
    }
  }
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  const content = element.textContent
  assert(content !== null, `Expected text content of node '${element.nodeName}', but was undefined`)
  return {
    id: attributeString(element, 'y.id'),
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processPkCode = processIdNode
const processAvisCodeName = processIdNode
const processCosimaCodeName = processIdNode

const processTextVariableDefinition = (element: Element | undefined): TextVariableDefinition | undefined => {
  if (!element) {
    return undefined
  }
  assertNodeProperties(element, { attributeNames: ['y.id'], childNames: ['y.textvar-def'] })
  const definitions = getElementChildren(element, 'y.textvar-def').map((x) => {
    assertNodeProperties(x, { attributeNames: ['wert', 'name', 'y.id'] })
    return {
      type: 'y.textvar-def',
      id: attributeString(x, 'y.id'),
      name: attributeString(x, 'name'),
      value: attributeString(x, 'wert'),
    } satisfies TextVariableDefinition['definitions'][number]
  })

  return {
    type: 'y.textvar-defs',
    id: attributeString(element, 'y.id'),
    definitions: definitions,
  }
}

const processMetaFigure = (element: Element | undefined): MetaFigureComponent => {
  if (!element) {
    return {
      type: 'meta-figure',
      id: 'NOT FOUND',
      grid: 'NOT FOUND',
      domainGrid: 'NOT FOUND',
      images: [],
    }
  }
  assertNodeProperties(element, { attributeNames: ['y.constraint.domain.grid', 'y.id', 'grid'] })
  const images = processImages(getElementChildren(element, 'img'))
  return {
    type: 'meta-figure',
    id: attributeString(element, 'y.id'),
    grid: attributeString(element, 'grid'),
    domainGrid: attributeString(element, 'y.constraint.domain.grid'),
    images,
  }
}

const processImages = (elements: Element[]) => elements.map(processImage)

const processImage = (element: Element): ImageComponent => {
  assertNodeProperties(element, {
    attributeNames: ['y.io.type', 'y.reftype', 'y.id', 'y.ref.io.types', 'y.io.validity.mode', 'y.validity.mode', 'y.io.name', 'y.filename'],
  })
  return {
    type: attributeString(element, 'y.io.type'),
    refType: attributeString(element, 'y.reftype'),
    id: attributeString(element, 'y.id'),
    validityMode: attributeString(element, 'y.io.validity.mode'),
    name: attributeString(element, 'y.io.name'),
    fileName: attributeString(element, 'y.filename'),
  }
}

// 2024-10-10: these* are very much the same, I've kept them separate to be more convenient to hack around
// * ShortDescription, Benefits and Notes, that is
const processPkShortDescription = (element: Element): PkShortDescription => {
  return {
    ...processPkBase(element),
    content: [...element.children].map(processViewElement) as TopLevelComponent[],
  }
}

const processPkBenefits = (element: Element): PkBenefits => {
  return {
    ...processPkBase(element),
    content: [...element.children].map(processViewElement) as TopLevelComponent[],
  }
}

const processPkNotes = (element: Element): PkBenefits => {
  return {
    ...processPkBase(element),
    content: [...element.children].map(processViewElement) as TopLevelComponent[],
  }
}

const processPkDescription = (element: Element): PkDescription => {
  assertNodeProperties(element, { childNames: ['PK-block'] })
  return {
    ...processPkBase(element),
    block: getElementChildren(element, 'PK-block').map(processPkBlock),
  }
}

const processPkFunctions = (element: Element) => {
  assertNodeProperties(element, { childNames: ['PK-block'] })
  return {
    ...processPkBase(element),
    block: getElementChildren(element, 'PK-block').map(processPkBlock),
  }
}

const processPkTechdata = (element: Element): PkTechData => {
  assertNodeProperties(element, { childNames: ['table'] })
  const figures = getElementChildren(element, 'figure')
  const table = getElementChild(element, 'table')
  return {
    ...processPkBase(element),
    figure: figures.map(processFigure).filter((x) => !!x),
    table: processTable(table),
  }
}

const processPkBlock = (element: Element): PkBlock => {
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  const title = getElementChildOptional(element, 'title')?.textContent
  const figure = getElementChildOptional(element, 'figure')

  const viewComponents = []
  for (const child of element.children) {
    if (child.nodeName === 'title' || child.nodeName === 'figure') {
      continue
    }

    viewComponents.push(processViewElement(child))
  }
  return {
    type: 'PK-block',
    id: attributeString(element, 'y.id'),
    title: title ?? undefined,
    content: viewComponents as (ParagraphComponent | UnorderedListComponent)[],
    figure: processFigure(figure),
  }
}

// and now for the tricky bit: lexing content

const processViewElement = (element: Element) => {
  switch (element.nodeName) {
    case 'p':
      return processParagraph(element)
    case 'sup':
      return processSup(element)
    case 'sub':
      return processSub(element)
    case 'schar':
      return processSpecialCharacter(element)
    case 'highlight':
      return processHighlight(element)
    case 'nt':
      return processNoTranslate(element)
    case 'vcode-link':
      return processVcodeLink(element)
    case 'table':
      return processTable(element)
    case 'tgroup':
      return processTGroup(element)
    case 'colspec':
      return processColspec(element)
    case 'row':
      return processRow(element)
    case 'entry':
      return processEntry(element)
    case 'thead':
      return processTHead(element)
    case 'tbody':
      return processTBody(element)
    case 'ul':
      return processUnorderedList(element)
    case 'li':
      return processListItem(element)
    case 'figure':
      return processFigure(element)
  }
  fail(`Unexpected element node of type '${element.nodeName}'`)
  return {
    type: 'unknown',
  } satisfies UnknownComponent
}

const processParagraph = (element: Element): ParagraphComponent => {
  const components: ComplexParagraphComponent['components'] = []
  const nodes = [...element.children]
  for (const child of element.childNodes) {
    if (child.nodeName === '#text') {
      components.push({
        type: 'p',
        text: child.textContent ?? 'NOT FOUND',
      })
    } else {
      const nextElement = nodes.shift()
      if (!nextElement) {
        fail('There should have been a render node in this paragraph')
        continue
      }
      components.push(processViewElement(nextElement) as ComplexParagraphComponent['components'][number])
    }
  }

  switch (components.length) {
    // there are empty paragraphs sometimes
    case 0:
      return {
        type: 'p',
        text: '',
      }
    case 1:
      return components[0] as ParagraphComponent
    default:
      return { type: 'combined-p', components: components }
  }
}

const processSup = (element: Element): SupComponent => {
  return {
    type: 'sup',
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processSub = (element: Element): SubComponent => {
  return {
    type: 'sub',
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processSpecialCharacter = (element: Element): SpecialCharacterComponent => {
  assertNodeProperties(element, { attributeNames: ['font', 'nr'] })
  return {
    type: 'schar',
    font: attributeString(element, 'font'),
    nr: attributeString(element, 'nr'),
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processNoTranslate = (element: Element): NoTranslateComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id', 'translate', 'dir'] })
  return {
    type: 'nt',
    id: attributeString(element, 'y.id'),
    translate: attributeString(element, 'translate'),
    dir: attributeString(element, 'dir'),
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processHighlight = (element: Element): HighlightComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  return {
    type: 'highlight',
    id: attributeString(element, 'y.id'),
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processVcodeLink = (element: Element): VCodeLinkComponent => {
  assertNodeProperties(element, {
    attributeNames: ['y.classification', 'y.id', 'y.io.name', 'y.link.required', 'y.linktype', 'y.target.id'],
  })
  return {
    type: 'vcode-link',
    id: attributeString(element, 'y.id'),
    classification: attributeString(element, 'y.classification'),
    name: attributeString(element, 'y.io.name'),
    linkRequired: attributeBoolean(element, 'y.link.required') ?? false,
    linkType: attributeString(element, 'y.linktype'),
    targetId: attributeString(element, 'y.target.id'),
  }
}

const processTable = (element: Element): TableComponent => {
  assertNodeProperties(element, {
    childNames: ['tgroup'],
    attributeNames: ['y.constraint.domain.size', 'y.constraint.domain.table-style', 'y.id'],
  })
  return {
    type: 'table',
    title: getElementChildOptional(element, 'title')?.textContent ?? undefined,
    size: attributeNumberOptional(element, 'size'),
    constraintDomainSize: attributeString(element, 'y.constraint.domain.size'),
    constraintDomainTableSize: attributeString(element, 'y.constraint.domain.table-style'),
    content: getElementChildren(element, 'tgroup').map(processTGroup),
  }
}

const processTGroup = (element: Element): TableGroupComponent => {
  assertNodeProperties(element, { childNames: ['colspec', 'tbody'], attributeNames: ['cols'] })
  const columns = getElementChildren(element, 'colspec')
  const header = getElementChildOptional(element, 'thead')
  const body = getElementChild(element, 'tbody')
  return {
    type: 'tgroup',
    columnCount: attributeNumber(element, 'cols'),
    columns: columns.map(processColspec),
    align: attributeStringOptional(element, 'align'),
    header: header ? processTHead(header) : undefined,
    body: processTBody(body),
  }
}

const processColspec = (element: Element): ColspecComponent => {
  assertNodeProperties(element, { attributeNames: ['align', 'colname', 'colnum', 'colwidth'] })
  return {
    type: 'colspec',
    align: attributeString(element, 'align'),
    name: attributeString(element, 'colname'),
    num: attributeNumber(element, 'colnum'),
    width: attributeString(element, 'colwidth'),
  }
}

const processTHead = (element: Element): TableHeadComponent => {
  assertNodeProperties(element, { childNames: ['row'] })
  return [...element.children].map(processRow)
}

const processTBody = (element: Element): TableBodyComponent => {
  assertNodeProperties(element, { childNames: ['row'] })
  return [...element.children].map(processRow)
}

const processRow = (element: Element): RowComponent => {
  assertNodeProperties(element, { childNames: ['entry'] })
  const entries = getElementChildren(element, 'entry')
  return {
    type: 'row',
    valign: element.getAttribute('valign') ?? undefined,
    entries: entries.map(processEntry),
  }
}

const processEntry = (element: Element): EntryComponent => {
  assertNodeProperties(element, { attributeNames: ['colname', 'y.id'] })
  const paragraphs = getElementChildren(element, 'p')
  return {
    type: 'entry',
    colname: attributeString(element, 'colname'),
    id: attributeString(element, 'y.id'),
    content: paragraphs.map(processViewElement) as (ParagraphComponent | UnorderedListComponent)[],
  }
}

const processUnorderedList = (element: Element): UnorderedListComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  const title = getElementChildOptional(element, 'title')
  const entries = [...element.children].filter((x) => x.nodeName === 'p' || x.nodeName === 'li')
  return {
    type: 'ul',
    id: attributeString(element, 'y.id'),
    title: title?.textContent ?? undefined,
    entries: entries.map((x) => (x.nodeName === 'p' ? processParagraph(x) : processListItem(x))),
  }
}

const processListItem = (element: Element): ListItemComponent => {
  assertNodeProperties(element, { childNames: ['p'], attributeNames: ['y.id'] })
  const paragraphs = getElementChildren(element, 'p')
  return {
    type: 'li',
    id: attributeString(element, 'y.id'),
    content: paragraphs.map(processParagraph),
  }
}

const processFigure = (element: Element | undefined): FigureComponent | undefined => {
  if (!element) {
    return undefined
  }
  assertNodeProperties(element, { childNames: ['img'], attributeNames: ['y.constraint.domain.size', 'y.id'] })
  const subtitle = processSubtitle(getElementChildOptional(element, 'subtitle'))
  const legend = processLegend(getElementChildOptional(element, 'legend'))
  return {
    type: 'figure',
    id: attributeString(element, 'y.id'),
    size: attributeNumberOptional(element, 'size'),
    domainSize: attributeString(element, 'y.constraint.domain.size'),
    images: getElementChildren(element, 'img').map(processImage),
    subtitle,
    legend,
  }
}

const processSubtitle = (element: Element | undefined): SubtitleComponent | undefined => {
  if (!element) {
    return undefined
  }
  assertNodeProperties(element, { attributeNames: ['y.id', 'y.commentable'] })
  return {
    type: 'subtitle',
    id: attributeString(element, 'y.id'),
    commentable: attributeBoolean(element, 'y.commentable') ?? false,
    content: element.textContent ?? 'NOT FOUND',
  }
}

const processLegend = (element: Element | undefined): LegendComponent | undefined => {
  if (!element) {
    return undefined
  }
  assertNodeProperties(element, { attributeNames: ['y.id', 'hide', 'y.commentable'], childNames: ['legentry'] })
  const entries = getElementChildren(element, 'legentry')
  return {
    type: 'legend',
    id: attributeString(element, 'y.id'),
    hide: attributeBoolean(element, 'hide') ?? false,
    commentable: attributeBoolean(element, 'y.commentable') ?? false,
    entries: entries.map(processLegendEntry),
  }
}

const processLegendEntry = (element: Element): LegendEntryComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id', 'y.linktarget'], childNames: ['sy', 'desc'] })
  const sy = getElementChild(element, 'sy')
  const desc = getElementChild(element, 'desc')
  return {
    type: 'legentry',
    linkTarget: attributeBoolean(element, 'y.linktarget') ?? false,
    sy: processLegendEntrySy(sy),
    description: processLegendEntryDescription(desc),
  }
}

const processLegendEntrySy = (element: Element): LegendEntrySyComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  return {
    type: 'sy',
    id: attributeString(element, 'y.id'),
    value: element.textContent ?? 'NOT FOUND',
  }
}

const processLegendEntryDescription = (element: Element): LegendEntryDescriptionComponent => {
  assertNodeProperties(element, { attributeNames: ['y.id'] })
  return {
    type: 'desc',
    id: attributeString(element, 'y.id'),
    content: processParagraph(getElementChild(element, 'p')),
  }
}
